import React from "react";
import { Reveal } from "../../lib/motion";
import { useApp } from "@/context/AppContext";

export default function Footer() {
  const { t } = useApp();
  // Phase 3I.4O.4 — cross-route aware. Footer is now rendered on both the
  // homepage and /optimise (and future non-home pages). If the current
  // route is not the homepage, hash links must bounce back to `/` first
  // so PublicSite's hash-scroll effect can settle to the correct section.
  const go = (href) => {
    const onHome = window.location.pathname === "/";
    if (!onHome && href && href.startsWith("#")) {
      window.location.href = "/" + href;
      return;
    }
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };
  const year = new Date().getFullYear();

  const links = [
    { label: t("nav.crm"), href: "#crm-service" },
    { label: t("nav.websites"), href: "#websites" },
    { label: t("nav.landing"), href: "#landing" },
    { label: t("nav.work"), href: "#work" },
    { label: t("nav.care"), href: "#care" },
    { label: t("nav.contact"), href: "#discovery" },
  ];

  return (
    <footer data-testid="site-footer" className="relative text-black">
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-20 pb-10">
        <Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-16 pb-16 border-b border-black/15">
            <div className="md:col-span-1">
              <p
                className="inline-flex items-center"
                data-testid="footer-logo"
              >
                <img
                  src="/orangebysite-logo-white-black-v2.webp"
                  alt="ORANGEBYSITE"
                  className="h-14 md:h-16 w-auto select-none"
                  draggable={false}
                />
              </p>
              <p className="text-black/75 text-[14px] mt-4 max-w-xs leading-relaxed font-medium">
                {t("footer.premium")}
              </p>
            </div>

            <div className="md:col-span-1">
              <p className="text-[11px] tracking-[0.22em] uppercase text-black/70 mb-5 font-bold">
                {t("footer.navigate")}
              </p>
              <ul className="space-y-3">
                {links.map((l) => (
                  <li key={l.href}>
                    <button
                      data-testid={`footer-link-${l.href.slice(1)}`}
                      onClick={() => go(l.href)}
                      className="text-black/85 hover:text-black text-[14px] font-semibold transition-colors"
                    >
                      {l.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="md:col-span-1">
              <p className="text-[11px] tracking-[0.22em] uppercase text-black/70 mb-5 font-bold">
                {t("footer.contact")}
              </p>
              <p className="text-black/80 text-[15px] leading-relaxed">
                Have a project in mind?
                <br />
                <a
                  data-testid="footer-contact-cta"
                  href="#discovery"
                  onClick={(e) => {
                    e.preventDefault();
                    const onHome = window.location.pathname === "/";
                    if (!onHome) { window.location.href = "/#discovery"; return; }
                    const el = document.querySelector("#discovery");
                    if (!el) return;
                    const header = document.querySelector('[data-testid="fixed-header"]');
                    const headerH = header ? header.getBoundingClientRect().height : 132;
                    const y = el.getBoundingClientRect().top + window.pageYOffset - headerH - 8;
                    window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
                  }}
                  className="mt-1 inline-block text-black font-black text-base underline underline-offset-4 decoration-2 hover:opacity-70 transition-opacity cursor-pointer tracking-wide uppercase"
                >
                  Contact us
                </a>
              </p>
            </div>
          </div>
        </Reveal>

        {/* Restored giant footer wordmark — clamp-sized so nothing clips */}
        <Reveal>
          <div
            data-testid="footer-brandmark"
            aria-hidden
            className="relative mt-16 mb-6 text-center select-none w-full overflow-hidden"
          >
            <div
              className="font-display font-black uppercase text-black"
              style={{ fontSize: "clamp(0.9rem, 2.4vw, 1.8rem)", letterSpacing: "0.42em" }}
            >
              YOUR&nbsp;BUSINESS.
            </div>
            <div
              className="font-display font-black leading-[0.86] mt-3 whitespace-nowrap"
              style={{
                fontSize: "clamp(2.4rem, 12vw, 11rem)",
                letterSpacing: "-0.045em",
                color: "#7A2500",
                opacity: 0.6,
              }}
            >
              ONE&nbsp;SYSTEM.
            </div>
          </div>
        </Reveal>

        <div className="mt-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-[12px] text-black/70 font-semibold">
          <p>© {year} ORANGEBYSITE™. {t("footer.rights")}</p>
          <p>{t("footer.tagline")}</p>
        </div>
      </div>
    </footer>
  );
}
